import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import { jwtDecode } from "jwt-decode";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DataObjectRoundedIcon from "@mui/icons-material/DataObjectRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { getAuthToken } from "../utils/session";
import SelectAllRoundedIcon from "@mui/icons-material/SelectAllRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";

const FONT_FAMILY =
  'Inter, Manrope, "SF Pro Display", "Segoe UI", Roboto, Arial, sans-serif';

const COLORS = {
  page: "#F7F9FC",
  pageSoft: "#FFFFFF",
  surface: "#FFFFFF",
  surfaceSoft: "#FBFCFE",
  surfaceStrong: "#FFFFFF",

  border: "rgba(15,36,56,0.10)",
  borderSoft: "rgba(15,36,56,0.07)",
  borderStrong: "rgba(15,36,56,0.16)",

  text: "#172033",
  textStrong: "#102840",
  muted: "#64748B",
  mutedSoft: "#94A3B8",

  primary: "#1E4B81",
  primaryDark: "#102840",
  primarySoft: "#EEF5FF",

  accent: "#D37D36",
  accentDark: "#B6632C",
  accentSoft: "#FFF4EA",

  success: "#14A67A",
  successSoft: "#EAFBF6",

  purple: "#5B5CE2",
  purpleSoft: "#F0F0FF",

  danger: "#D32F2F",
  dangerSoft: "#FFEBEE",

  shadow: "0 18px 48px rgba(15,36,56,0.06)",
  hoverShadow: "0 18px 42px rgba(15,36,56,0.08)",

  background:
    "radial-gradient(circle at 18% 0%, rgba(30,75,129,0.08), transparent 32%), radial-gradient(circle at 92% 8%, rgba(211,125,54,0.08), transparent 28%), linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 48%, #F7F9FC 100%)",
};

const TYPO = {
  pageTitle: 900,
  sectionTitle: 820,
  itemTitle: 760,
  selectedItem: 820,
  bodyStrong: 680,
  body: 520,
  meta: 650,
  chip: 800,
  button: 820,
};

const PLAN_CONFIG = {
  ROLE_FREEMIUM: {
    label: "Freemium",
    maxAutoevaluaciones: 1,
  },
  ROLE_PREMIUM: {
    label: "Premium",
    maxAutoevaluaciones: 2,
  },
  ROLE_PREMIUM_PRO: {
    label: "Premium Pro",
    maxAutoevaluaciones: Infinity,
  },
  ROLE_PREMIUMPRO: {
    label: "Premium Pro",
    maxAutoevaluaciones: Infinity,
  },
  ROLE_ADMIN: {
    label: "Admin",
    maxAutoevaluaciones: Infinity,
  },
};

const NIVEL_DESCRIPTIONS = {
  1: "Realizado",
  2: "Gestionado",
  3: "Establecido",
  4: "Predecible",
  5: "Optimizado",
};

const TIPO_CONFIG = {
  Gobierno: {
    label: "Gobierno",
    color: COLORS.primary,
    bg: COLORS.primarySoft,
    icon: <ShieldRoundedIcon sx={{ fontSize: 14 }} />,
  },
  Gestión: {
    label: "Gestión",
    color: COLORS.accent,
    bg: COLORS.accentSoft,
    icon: <DataObjectRoundedIcon sx={{ fontSize: 14 }} />,
  },
  Gestion: {
    label: "Gestión",
    color: COLORS.accent,
    bg: COLORS.accentSoft,
    icon: <DataObjectRoundedIcon sx={{ fontSize: 14 }} />,
  },
  Calidad: {
    label: "Calidad",
    color: COLORS.success,
    bg: COLORS.successSoft,
    icon: <CheckCircleRoundedIcon sx={{ fontSize: 14 }} />,
  },
};

const getPlanConfig = (role) => PLAN_CONFIG[role] || PLAN_CONFIG.ROLE_FREEMIUM;

const getTipoConfig = (tipo) => {
  return (
    TIPO_CONFIG[tipo] || {
      label: tipo || "Proceso",
      color: COLORS.primary,
      bg: COLORS.primarySoft,
      icon: <DescriptionRoundedIcon sx={{ fontSize: 14 }} />,
    }
  );
};

const panelSx = {
  bgcolor: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: "22px",
  boxShadow: COLORS.shadow,
};

const primaryButtonSx = {
  minHeight: 42,
  borderRadius: "14px",
  px: 2.3,
  textTransform: "none",
  fontWeight: TYPO.button,
  fontSize: "0.76rem",
  fontFamily: FONT_FAMILY,
  color: COLORS.surface,
  background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.accentDark})`,
  boxShadow: "0 14px 32px rgba(211,125,54,0.22)",

  "&:hover": {
    background: `linear-gradient(135deg, ${COLORS.accentDark}, #8A4514)`,
    boxShadow: "0 18px 40px rgba(211,125,54,0.28)",
  },

  "&.Mui-disabled": {
    bgcolor: "rgba(100,116,139,0.16)",
    background: "rgba(100,116,139,0.16)",
    color: "rgba(100,116,139,0.78)",
    boxShadow: "none",
  },
};

const secondaryButtonSx = {
  minHeight: 40,
  borderRadius: "13px",
  px: 1.7,
  textTransform: "none",
  fontWeight: TYPO.button,
  fontSize: "0.76rem",
  fontFamily: FONT_FAMILY,
  color: COLORS.primary,
  borderColor: "rgba(30,75,129,0.22)",
  bgcolor: COLORS.surface,

  "&:hover": {
    borderColor: COLORS.primary,
    bgcolor: COLORS.primarySoft,
  },
};

const textButtonSx = {
  minHeight: 40,
  borderRadius: "13px",
  px: 1.4,
  textTransform: "none",
  fontWeight: TYPO.button,
  fontSize: "0.76rem",
  fontFamily: FONT_FAMILY,
  color: COLORS.muted,

  "&:hover": {
    bgcolor: COLORS.surfaceSoft,
    color: COLORS.textStrong,
  },
};

const NuevaAutoevaluacion = () => {
  const navigate = useNavigate();

  const [procesos, setProcesos] = useState([]);
  const [selectedProcesos, setSelectedProcesos] = useState([]);
  const [userRole, setUserRole] = useState("ROLE_FREEMIUM");
  const [userId, setUserId] = useState(null);
  const [numAutoevaluaciones, setNumAutoevaluaciones] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const plan = useMemo(() => getPlanConfig(userRole), [userRole]);
  const maxAutoevaluaciones = plan.maxAutoevaluaciones;
  const disableButtons = numAutoevaluaciones >= maxAutoevaluaciones;

  const totalProcesos = procesos.length;
  const selectedCount = selectedProcesos.length;
  const selectionProgress = totalProcesos
    ? Math.round((selectedCount / totalProcesos) * 100)
    : 0;

  useEffect(() => {
    document.body.style.background = COLORS.page;
  }, []);

  useEffect(() => {
    const fetchProcesos = async (id) => {
      const response = await axiosInstance.get(API_ENDPOINTS.procesos.porUsuario(id));
      setProcesos(response.data || []);
    };

    const fetchAutoevaluaciones = async (id, token) => {
      const response = await axiosInstance.get(API_ENDPOINTS.autoevaluaciones.resumen(id), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setNumAutoevaluaciones(response.data?.autoevaluaciones?.length || 0);
    };

    const initializeUser = async () => {
      try {
        setLoading(true);
        setError("");

        const token = getAuthToken();
        if (!token) throw new Error("No se ha encontrado la sesión del usuario.");

        const decodedToken = jwtDecode(token);
        const id = decodedToken.id || decodedToken.userId || decodedToken._id;

        if (!id) throw new Error("No se ha podido identificar al usuario.");

        setUserId(id);
        setUserRole(decodedToken.role || "ROLE_FREEMIUM");

        await Promise.all([fetchAutoevaluaciones(id, token), fetchProcesos(id)]);
      } catch (error) {
        console.error("Error al inicializar la autoevaluación:", error);
        setError("No hemos podido cargar los procesos disponibles. Inténtalo de nuevo más tarde.");
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, []);

  const filteredProcesos = useMemo(() => {
    if (!searchQuery.trim()) return procesos;

    const normalizedSearch = searchQuery.trim().toLowerCase();

    return procesos.filter((proceso) => {
      const nombre = proceso.nombre || "";
      const abreviatura = proceso.abreviatura || "";
      const tipo = proceso.tipo || "";
      const nivel = proceso.nivel || "";

      return `${nombre} ${abreviatura} ${tipo} ${nivel}`
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [procesos, searchQuery]);

  const groupedProcesos = useMemo(() => {
    return filteredProcesos.reduce((acc, proceso) => {
      const nivel = proceso.nivel || "Sin nivel";

      if (!acc[nivel]) acc[nivel] = [];
      acc[nivel].push(proceso);

      return acc;
    }, {});
  }, [filteredProcesos]);

  const sortedNivelKeys = useMemo(() => {
    return Object.keys(groupedProcesos).sort((a, b) => {
      const numA = Number(a);
      const numB = Number(b);

      if (Number.isNaN(numA) || Number.isNaN(numB)) {
        return String(a).localeCompare(String(b));
      }

      return numA - numB;
    });
  }, [groupedProcesos]);

  const toggleProceso = (procesoId) => {
    if (disableButtons) return;

    setSelectedProcesos((prev = []) =>
      prev.includes(procesoId)
        ? prev.filter((id) => id !== procesoId)
        : [...prev, procesoId]
    );
  };

  const toggleSeleccionarVisibles = () => {
    if (disableButtons) return;

    const visibleIds = filteredProcesos.map((p) => p._id);
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedProcesos.includes(id));

    if (allVisibleSelected) {
      setSelectedProcesos((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }

    setSelectedProcesos((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const clearSelection = () => {
    if (disableButtons) return;
    setSelectedProcesos([]);
  };

  const iniciarAutoevaluacion = async () => {
    if (selectedProcesos.length === 0) {
      Swal.fire({
        title: "Selecciona al menos un proceso",
        text: "Elige los procesos que quieres evaluar antes de continuar.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: COLORS.accent,
      });
      return;
    }

    try {
      setCreating(true);

      const token = getAuthToken();

      const response = await axiosInstance.post(
        API_ENDPOINTS.autoevaluaciones.crear,
        {
          userId,
          procesos: selectedProcesos,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const cuestionarioId = response.data.cuestionario._id;
      navigate(`/instrucciones/${cuestionarioId}`);
    } catch (error) {
      Swal.fire({
        title: "No se pudo iniciar la autoevaluación",
        text: "Ha ocurrido un error al crear el cuestionario. Inténtalo de nuevo.",
        icon: "error",
        confirmButtonText: "Entendido",
        confirmButtonColor: COLORS.danger,
      });
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <LoadingScreen />;

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: COLORS.background,
          pt: { xs: 10, md: 4 },
          p: 3,
          fontFamily: FONT_FAMILY,
        }}
      >
        <Container maxWidth="xl" disableGutters sx={{ px: { xs: 1.25, sm: 1.75, md: 2 } }}>
          <Alert
            severity="error"
            sx={{
              borderRadius: 4,
              border: `1px solid ${COLORS.border}`,
            }}
          >
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: COLORS.background,
        pt: { xs: 9, md: 3 },
        pb: { xs: 13, md: 11 },
        fontFamily: FONT_FAMILY,
        color: COLORS.text,
      }}
    >
      <Container maxWidth="xl" disableGutters sx={{ px: { xs: 1.25, sm: 1.75, md: 2 } }}>
        <HeaderBar
          selectedCount={selectedCount}
          totalProcesos={totalProcesos}
          selectionProgress={selectionProgress}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          disableButtons={disableButtons}
          filteredCount={filteredProcesos.length}
          onSelectVisible={toggleSeleccionarVisibles}
          onClear={clearSelection}
        />

        {disableButtons && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: "16px",
              bgcolor: COLORS.surface,
              border: "1px solid rgba(211,125,54,0.22)",
              fontFamily: FONT_FAMILY,
            }}
          >
            Has alcanzado el límite de autoevaluaciones de tu plan {plan.label}.
          </Alert>
        )}

        {filteredProcesos.length === 0 ? (
          <EmptySearchState />
        ) : userRole === "ROLE_FREEMIUM" ? (
          <CompactGrid
            procesos={filteredProcesos}
            selectedProcesos={selectedProcesos}
            disabled={disableButtons}
            onToggle={toggleProceso}
          />
        ) : (
          <Stack spacing={1.5}>
            {sortedNivelKeys.map((nivel) => (
              <NivelAccordion
                key={nivel}
                nivel={nivel}
                procesos={groupedProcesos[nivel]}
                selectedProcesos={selectedProcesos}
                disabled={disableButtons}
                onToggle={toggleProceso}
              />
            ))}
          </Stack>
        )}
      </Container>

      <BottomActionBar
        selectedCount={selectedCount}
        totalProcesos={totalProcesos}
        disableButtons={disableButtons}
        creating={creating}
        onStart={iniciarAutoevaluacion}
      />
    </Box>
  );
};

const HeaderBar = ({
  selectedCount,
  totalProcesos,
  selectionProgress,
  searchQuery,
  setSearchQuery,
  disableButtons,
  filteredCount,
  onSelectVisible,
  onClear,
}) => (
  <Paper
    elevation={0}
    sx={{
      ...panelSx,
      p: { xs: 1.35, md: 1.75 },
      mb: 2,
      position: "sticky",
      top: { xs: 76, md: 12 },
      zIndex: 20,
      bgcolor: "rgba(255,255,255,0.92)",
      backdropFilter: "blur(18px)",
    }}
  >
    <Stack spacing={1.45}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.4}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Breadcrumbs
            separator={<NavigateNextRoundedIcon fontSize="small" sx={{ color: COLORS.muted }} />}
            aria-label="breadcrumb"
            sx={{ mb: 0.45 }}
          >
            <MuiLink
              component={RouterLink}
              to="/mis-autoevaluaciones"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: COLORS.primary,
                fontWeight: TYPO.meta,
                fontSize: "0.76rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              <HomeRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Dashboard
            </MuiLink>

            <Typography
              sx={{
                color: COLORS.muted,
                fontWeight: TYPO.meta,
                fontSize: "0.76rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              Procesos
            </Typography>
          </Breadcrumbs>

          <Typography
            sx={{
              color: COLORS.textStrong,
              fontWeight: TYPO.pageTitle,
              fontSize: { xs: "1.2rem", md: "1.48rem" },
              letterSpacing: "-0.045em",
              lineHeight: 1.1,
              fontFamily: FONT_FAMILY,
            }}
          >
            Selecciona los procesos
          </Typography>

          <Typography
            sx={{
              color: COLORS.muted,
              fontSize: "0.78rem",
              lineHeight: 1.55,
              mt: 0.45,
              fontFamily: FONT_FAMILY,
            }}
          >
            Define el alcance de la autoevaluación eligiendo los procesos que quieres analizar.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={0.9}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <ProcessCounter
            selectedCount={selectedCount}
            totalProcesos={totalProcesos}
            filteredCount={filteredCount}
          />

          <Tooltip title={disableButtons ? "Has alcanzado el límite de autoevaluaciones" : ""} arrow>
            <span>
              <Button
                variant="outlined"
                startIcon={<SelectAllRoundedIcon sx={{ fontSize: 18 }} />}
                onClick={onSelectVisible}
                disabled={disableButtons || filteredCount === 0}
                sx={secondaryButtonSx}
              >
                Seleccionar visibles
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="text"
            onClick={onClear}
            disabled={disableButtons || selectedCount === 0}
            sx={textButtonSx}
          >
            Limpiar
          </Button>
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.4}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <TextField
          fullWidth
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Buscar por nombre, abreviatura, tipo o nivel..."
          sx={{
            maxWidth: { xs: "100%", md: 470 },

            "& .MuiOutlinedInput-root": {
              height: 42,
              borderRadius: "14px",
              bgcolor: COLORS.surfaceSoft,
              fontWeight: TYPO.bodyStrong,
              fontSize: "0.76rem",
              fontFamily: FONT_FAMILY,

              "& fieldset": {
                borderColor: COLORS.border,
              },

              "&:hover fieldset": {
                borderColor: COLORS.primary,
              },

              "&.Mui-focused fieldset": {
                borderColor: COLORS.primary,
              },
            },

            "& .MuiInputBase-input": {
              fontSize: "0.76rem",
              fontFamily: FONT_FAMILY,
            },

            "& input::placeholder": {
              color: COLORS.muted,
              opacity: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: COLORS.muted, fontSize: 19 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery("")}>
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography
              sx={{
                color: COLORS.muted,
                fontWeight: TYPO.meta,
                fontSize: "0.72rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              Selección
            </Typography>

            <Typography
              sx={{
                color: COLORS.textStrong,
                fontWeight: TYPO.sectionTitle,
                fontSize: "0.72rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              {selectionProgress}%
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={selectionProgress}
            sx={{
              height: 7,
              borderRadius: 99,
              bgcolor: "rgba(15,36,56,0.08)",

              "& .MuiLinearProgress-bar": {
                borderRadius: 99,
                background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.accent})`,
              },
            }}
          />
        </Box>
      </Stack>
    </Stack>
  </Paper>
);

const ProcessCounter = ({ selectedCount, totalProcesos, filteredCount }) => (
  <Box
    sx={{
      px: 1.3,
      py: 0.8,
      borderRadius: "14px",
      bgcolor: COLORS.primarySoft,
      border: "1px solid rgba(30,75,129,0.12)",
      minWidth: 146,
    }}
  >
    <Typography
      sx={{
        color: COLORS.primary,
        fontWeight: TYPO.pageTitle,
        fontSize: "0.82rem",
        lineHeight: 1.1,
        fontFamily: FONT_FAMILY,
      }}
    >
      {selectedCount}/{totalProcesos} seleccionados
    </Typography>

    <Typography
      sx={{
        color: COLORS.muted,
        fontWeight: TYPO.meta,
        fontSize: "0.66rem",
        mt: 0.25,
        fontFamily: FONT_FAMILY,
      }}
    >
      {filteredCount} visibles
    </Typography>
  </Box>
);

const CompactGrid = ({ procesos, selectedProcesos, disabled, onToggle }) => (
  <Grid container spacing={1.35}>
    {procesos.map((proceso) => (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={proceso._id}>
        <ProcesoCard
          proceso={proceso}
          selected={selectedProcesos.includes(proceso._id)}
          disabled={disabled}
          onClick={() => onToggle(proceso._id)}
        />
      </Grid>
    ))}
  </Grid>
);

const NivelAccordion = ({ nivel, procesos, selectedProcesos, disabled, onToggle }) => {
  const selectedInNivel = procesos.filter((proceso) =>
    selectedProcesos.includes(proceso._id)
  ).length;

  const nivelTitle = NIVEL_DESCRIPTIONS[nivel] || "Nivel de capacidad";

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        ...panelSx,
        borderRadius: "20px !important",
        overflow: "hidden",

        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon sx={{ color: COLORS.primary }} />}
        sx={{
          px: { xs: 1.6, md: 1.9 },
          py: 0.7,
          minHeight: 54,
          bgcolor: COLORS.surface,
          borderBottom: `1px solid ${COLORS.border}`,

          "& .MuiAccordionSummary-content": {
            my: 0,
            alignItems: "center",
          },
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "12px",
              bgcolor: COLORS.primarySoft,
              color: COLORS.primary,
              display: "grid",
              placeItems: "center",
              fontWeight: TYPO.pageTitle,
              fontSize: "0.82rem",
              border: "1px solid rgba(30,75,129,0.14)",
              flexShrink: 0,
              fontFamily: FONT_FAMILY,
            }}
          >
            {nivel}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              noWrap
              sx={{
                color: COLORS.textStrong,
                fontWeight: TYPO.pageTitle,
                fontSize: "0.92rem",
                letterSpacing: "-0.025em",
                fontFamily: FONT_FAMILY,
              }}
            >
              Nivel {nivel} — {nivelTitle}
            </Typography>

            <Typography
              sx={{
                color: COLORS.muted,
                fontWeight: TYPO.meta,
                fontSize: "0.7rem",
                mt: 0.1,
                fontFamily: FONT_FAMILY,
              }}
            >
              {procesos.length} procesos disponibles
            </Typography>
          </Box>

          <Chip
            label={`${selectedInNivel}/${procesos.length}`}
            size="small"
            sx={{
              height: 23,
              bgcolor: selectedInNivel ? COLORS.accentSoft : COLORS.surfaceSoft,
              color: selectedInNivel ? COLORS.accent : COLORS.muted,
              border: `1px solid ${
                selectedInNivel ? "rgba(211,125,54,0.20)" : COLORS.border
              }`,
              fontWeight: TYPO.chip,
              fontSize: "0.68rem",
              fontFamily: FONT_FAMILY,
            }}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ p: { xs: 1.4, md: 1.6 } }}>
        <CompactGrid
          procesos={procesos}
          selectedProcesos={selectedProcesos}
          disabled={disabled}
          onToggle={onToggle}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const ProcesoCard = ({ proceso, selected, disabled, onClick }) => {
  const tipo = getTipoConfig(proceso.tipo);

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        minHeight: 116,
        height: "100%",
        p: 1.3,
        borderRadius: "18px",
        cursor: disabled ? "not-allowed" : "pointer",
        position: "relative",
        overflow: "hidden",
        bgcolor: COLORS.surface,
        border: selected ? `2px solid ${COLORS.accent}` : `1px solid ${COLORS.border}`,
        boxShadow: selected
          ? "0 14px 34px rgba(211,125,54,0.16)"
          : "0 10px 28px rgba(15,36,56,0.045)",
        opacity: disabled ? 0.58 : 1,
        transition: "all 180ms ease",

        "&:hover": disabled
          ? {}
          : {
              transform: "translateY(-2px)",
              boxShadow: selected
                ? "0 18px 42px rgba(211,125,54,0.20)"
                : COLORS.hoverShadow,
              borderColor: selected ? COLORS.accent : "rgba(30,75,129,0.22)",
            },
      }}
    >
      <Stack spacing={1} sx={{ height: "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Chip
            icon={tipo.icon}
            label={tipo.label}
            size="small"
            sx={{
              height: 23,
              maxWidth: "calc(100% - 34px)",
              bgcolor: tipo.bg,
              color: tipo.color,
              border: `1px solid ${tipo.color}22`,
              fontWeight: TYPO.chip,
              fontSize: "0.66rem",
              fontFamily: FONT_FAMILY,

              "& .MuiChip-icon": {
                color: tipo.color,
                ml: 0.6,
              },

              "& .MuiChip-label": {
                px: 0.8,
              },
            }}
          />

          <Box
            sx={{
              width: 25,
              height: 25,
              borderRadius: "9px",
              display: "grid",
              placeItems: "center",
              bgcolor: selected ? COLORS.accent : COLORS.surfaceSoft,
              color: selected ? COLORS.surface : COLORS.mutedSoft,
              border: selected ? "none" : `1px solid ${COLORS.border}`,
              flexShrink: 0,
            }}
          >
            <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
          </Box>
        </Stack>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            noWrap
            sx={{
              color: COLORS.textStrong,
              fontWeight: TYPO.pageTitle,
              fontSize: "0.92rem",
              letterSpacing: "-0.035em",
              mb: 0.35,
              fontFamily: FONT_FAMILY,
            }}
          >
            {proceso.abreviatura || "Proceso"}
          </Typography>

          <Typography
            sx={{
              color: COLORS.text,
              fontSize: "0.74rem",
              fontWeight: TYPO.bodyStrong,
              lineHeight: 1.38,
              fontFamily: FONT_FAMILY,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {proceso.nombre || "Proceso de datos"}
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            sx={{
              color: COLORS.muted,
              fontWeight: TYPO.meta,
              fontSize: "0.68rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            Nivel {proceso.nivel || "—"}
          </Typography>

          <Typography
            sx={{
              color: selected ? COLORS.accent : COLORS.mutedSoft,
              fontWeight: TYPO.chip,
              fontSize: "0.68rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            {selected ? "Seleccionado" : "Seleccionar"}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

const BottomActionBar = ({ selectedCount, totalProcesos, disableButtons, creating, onStart }) => (
  <Box
    sx={{
      position: "fixed",
      left: { xs: 12, md: 112 },
      right: { xs: 12, md: 24 },
      bottom: 16,
      zIndex: 1200,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.15, md: 1.3 },
        borderRadius: "22px",
        bgcolor: "rgba(255,255,255,0.92)",
        border: `1px solid ${COLORS.borderStrong}`,
        backdropFilter: "blur(18px)",
        boxShadow: "0 22px 70px rgba(15,36,56,0.16)",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Box>
          <Typography
            sx={{
              color: COLORS.textStrong,
              fontWeight: TYPO.pageTitle,
              fontSize: "0.9rem",
              letterSpacing: "-0.025em",
              fontFamily: FONT_FAMILY,
            }}
          >
            {selectedCount} de {totalProcesos} procesos seleccionados
          </Typography>

          <Typography
            sx={{
              color: COLORS.muted,
              fontWeight: TYPO.meta,
              fontSize: "0.72rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            Inicia la autoevaluación cuando tengas definido el alcance.
          </Typography>
        </Box>

        <Tooltip title={disableButtons ? "Has alcanzado el límite de autoevaluaciones" : ""} arrow>
          <span>
            <Button
              variant="contained"
              startIcon={
                creating ? (
                  <CircularProgress size={17} color="inherit" />
                ) : (
                  <RocketLaunchRoundedIcon sx={{ fontSize: 18 }} />
                )
              }
              onClick={onStart}
              disabled={disableButtons || creating}
              sx={primaryButtonSx}
            >
              {creating ? "Creando..." : "Iniciar autoevaluación"}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  </Box>
);

const EmptySearchState = () => (
  <Paper
    elevation={0}
    sx={{
      ...panelSx,
      p: { xs: 4, md: 5 },
      textAlign: "center",
    }}
  >
    <SearchRoundedIcon sx={{ color: COLORS.primary, fontSize: 40, mb: 1 }} />

    <Typography
      sx={{
        color: COLORS.textStrong,
        fontWeight: TYPO.pageTitle,
        fontSize: "1.12rem",
        mb: 0.75,
        fontFamily: FONT_FAMILY,
      }}
    >
      No hay procesos con ese criterio
    </Typography>

    <Typography
      sx={{
        color: COLORS.muted,
        fontWeight: TYPO.meta,
        fontSize: "0.8rem",
        fontFamily: FONT_FAMILY,
      }}
    >
      Prueba a buscar por otro nombre, abreviatura, tipo o nivel.
    </Typography>
  </Paper>
);

const LoadingScreen = () => (
  <Box
    sx={{
      minHeight: "100vh",
      background: COLORS.background,
      display: "grid",
      placeItems: "center",
      p: 3,
      fontFamily: FONT_FAMILY,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        p: 4,
        textAlign: "center",
        maxWidth: 390,
      }}
    >
      <CircularProgress sx={{ color: COLORS.primary, mb: 2 }} />

      <Typography
        sx={{
          color: COLORS.textStrong,
          fontWeight: TYPO.pageTitle,
          fontSize: "1.05rem",
          fontFamily: FONT_FAMILY,
        }}
      >
        Cargando procesos
      </Typography>

      <Typography
        sx={{
          color: COLORS.muted,
          fontWeight: TYPO.meta,
          mt: 0.75,
          fontSize: "0.8rem",
          lineHeight: 1.6,
          fontFamily: FONT_FAMILY,
        }}
      >
        Preparando la selección de procesos.
      </Typography>
    </Paper>
  </Box>
);

export default NuevaAutoevaluacion;
